import React, { useState, useRef, useEffect } from "react";
import {
  useCreateStagingPromptVersionMutation,
  useCreateProdPromptVersionMutation,
  usePinStagingVersionMutation,
  usePinProdVersionMutation,
} from "../../../../app/api/console/scanTemplateSyncApiSlice";

const PromptEditor = ({ prompt, environment, templateId, websiteName, onDelete }) => {
  const [newVersionText, setNewVersionText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const textareaRef = useRef(null);

  const [createStagingVersion, { isLoading: isCreatingStagingVersion }] = useCreateStagingPromptVersionMutation();
  const [createProdVersion, { isLoading: isCreatingProdVersion }] = useCreateProdPromptVersionMutation();
  const [pinStagingVersion, { isLoading: isPinningStagingVersion }] = usePinStagingVersionMutation();
  const [pinProdVersion, { isLoading: isPinningProdVersion }] = usePinProdVersionMutation();

  const isCreating = environment === "staging" ? isCreatingStagingVersion : isCreatingProdVersion;
  const isPinning = environment === "staging" ? isPinningStagingVersion : isPinningProdVersion;

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(0, 0);
    }
  }, [isEditing]);

  const handleCreateVersion = async () => {
    if (!newVersionText.trim()) return;
    if (newVersionText === prompt.current_prompt_text) {
      setIsEditing(false);
      return;
    }
    const createFn = environment === "staging" ? createStagingVersion : createProdVersion;
    await createFn({ promptId: prompt.prompt_id, version: { prompt_text: newVersionText } });
    setNewVersionText("");
    setIsEditing(false);
  };

  const handlePinVersion = async (versionId) => {
    const pinFn = environment === "staging" ? pinStagingVersion : pinProdVersion;
    await pinFn({ promptId: prompt.prompt_id, versionId });
  };

  const currentVersion = prompt.versions?.find(v => v.prompt_version_id === prompt.current_version_id);
  const currentVersionNumber = currentVersion?.version_number || prompt.versions?.length || 1;
  const promptText = prompt.current_prompt_text || "";
  const hasChanges = isEditing && newVersionText !== promptText;
  const isBase = !prompt.website_id;

  return (
    <div className={`rounded-xl border-2 transition-colors ${isBase ? "border-indigo-200 bg-white" : "border-amber-200 bg-white"}`}>
      {/* Header bar */}
      <div className={`flex items-center justify-between px-4 py-3 rounded-t-xl ${isBase ? "bg-indigo-50" : "bg-amber-50"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${isBase ? "bg-indigo-500" : "bg-amber-500"}`} />
          <h4 className="font-semibold text-gray-900 text-sm">
            {isBase ? "Base Prompt" : websiteName || prompt.website_id}
          </h4>
          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${isBase ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>
            v{currentVersionNumber}
          </span>
          {!isBase && (
            <span className="text-xs text-gray-400">website-specific</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{promptText.length} chars</span>
          {prompt.versions && prompt.versions.length > 1 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                showHistory ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {prompt.versions.length} versions
            </button>
          )}
          {!isEditing && (
            <>
              <button
                onClick={() => { setNewVersionText(promptText); setIsEditing(true); }}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  isBase ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-amber-600 text-white hover:bg-amber-700"
                }`}
              >
                Edit
              </button>
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                  title="Delete prompt"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Prompt content */}
      <div className="p-4">
        {isEditing ? (
          <div>
            <textarea
              ref={textareaRef}
              value={newVersionText}
              onChange={(e) => setNewVersionText(e.target.value)}
              rows={Math.min(Math.max(newVersionText.split("\n").length + 2, 8), 30)}
              className="w-full p-4 border-2 border-indigo-300 rounded-lg text-sm font-mono leading-relaxed focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 focus:outline-none resize-y"
              placeholder="Enter prompt text..."
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">{newVersionText.length} chars</span>
                {hasChanges && (
                  <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
                )}
                {!hasChanges && isEditing && (
                  <span className="text-xs text-gray-400">No changes</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setIsEditing(false); setNewVersionText(""); }}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateVersion}
                  disabled={isCreating || !newVersionText.trim() || !hasChanges}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreating ? "Saving..." : `Save as v${currentVersionNumber + 1}`}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            onClick={() => { setNewVersionText(promptText); setIsEditing(true); }}
            className="cursor-text group"
          >
            <pre className="p-4 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap font-mono leading-relaxed max-h-64 overflow-y-auto group-hover:bg-gray-100 transition-colors">
              {promptText || <span className="text-gray-400 italic">No prompt text — click to add</span>}
            </pre>
            <p className="text-xs text-gray-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Click to edit</p>
          </div>
        )}
      </div>

      {/* Version history (collapsed by default) */}
      {showHistory && prompt.versions && prompt.versions.length > 0 && (
        <div className="border-t px-4 py-3 bg-gray-50 rounded-b-xl">
          <div className="space-y-1.5">
            {prompt.versions.map((version) => {
              const isCurrent = version.prompt_version_id === prompt.current_version_id;
              const isExpanded = expandedVersion === version.prompt_version_id;
              return (
                <div key={version.prompt_version_id} className={`rounded-lg overflow-hidden ${isCurrent ? "ring-2 ring-indigo-300" : "border border-gray-200"}`}>
                  <div
                    className={`flex items-center justify-between px-3 py-2 cursor-pointer transition-colors ${
                      isCurrent ? "bg-indigo-50 hover:bg-indigo-100" : "bg-white hover:bg-gray-50"
                    }`}
                    onClick={() => setExpandedVersion(isExpanded ? null : version.prompt_version_id)}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        isCurrent ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-600"
                      }`}>
                        {version.version_number}
                      </span>
                      {isCurrent && (
                        <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">LIVE</span>
                      )}
                      <span className="text-xs text-gray-500">
                        {new Date(version.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      {version.created_by && (
                        <span className="text-xs text-gray-400">by {version.created_by}</span>
                      )}
                      <span className="text-xs text-gray-400">{version.prompt_text?.length || 0} chars</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isCurrent && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handlePinVersion(version.prompt_version_id); }}
                          disabled={isPinning}
                          className="px-2 py-1 text-xs font-medium text-indigo-600 hover:text-white hover:bg-indigo-600 rounded transition-colors disabled:opacity-50"
                        >
                          {isPinning ? "..." : "Restore"}
                        </button>
                      )}
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3 bg-white">
                      <pre className="p-3 bg-gray-50 rounded text-xs whitespace-pre-wrap font-mono max-h-48 overflow-y-auto leading-relaxed border">
                        {version.prompt_text}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptEditor;
